import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Text, Modal, Banner, Checkbox } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { useGetBatchesQuery, useStartReturningMutation, useResumeReturningMutation } from '../../../warehouse/store/batches';
import { getSessionEmail } from '../../../warehouse/utils/session';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';
import { UI_COLORS } from '../../../constants';

// The cart still holds every tote from picking - they show muted. Totes
// holding items to return (from returnItems) get the highlight colour.
const USED_TOTE_COLOR = UI_COLORS.cyan;
const RETURN_TOTE_COLOR = UI_COLORS.accent;

const getToteHighlights = (batch) => {
  // Totes are filled one row at a time, top to bottom, so the first
  // toteCount cells in row-major order are the ones in use
  const highlights = new Map(
    Array.from({ length: batch.toteCount }, (_, i) => {
      const row = Math.floor(i / batch.totalColumns) + 1;
      const column = (i % batch.totalColumns) + 1;
      return [`${row}-${column}`, { row, column, color: USED_TOTE_COLOR }];
    })
  );

  (batch.returnItems ?? []).forEach(entry => {
    highlights.set(`${entry.row}-${entry.column}`, {
      row: entry.row,
      column: entry.column,
      color: RETURN_TOTE_COLOR,
    });
  });

  return [...highlights.values()];
};

const ReturnBatches = () => {
  useDocumentTitle('Returns');
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the stock returns permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('stock_return');

  // The parked switch swaps the list from the returns queue to batches
  // parked mid-return - those can be claimed and resumed by any user
  const [showParked, setShowParked] = useState(false);

  // to_return batches wait for a returner; returning batches show up
  // read-only so a user sees what's being worked on - and can re-enter
  // their own
  const {
    data: toReturn = [],
    isLoading: toReturnLoading,
    error: toReturnError,
    isError: isToReturnError,
    refetch: refetchToReturn,
  } = useGetBatchesQuery('to_return', { skip: showParked });
  const {
    data: returning = [],
    isLoading: returningLoading,
    refetch: refetchReturning,
  } = useGetBatchesQuery('returning', { skip: showParked });
  const {
    data: parked = [],
    isLoading: parkedLoading,
    error: parkedError,
    isError: isParkedError,
    refetch: refetchParked,
  } = useGetBatchesQuery('returning_parked', { skip: !showParked });
  const [startReturning, { isLoading: isClaiming }] = useStartReturningMutation();
  const [resumeReturning, { isLoading: isResuming }] = useResumeReturningMutation();

  // A batch can briefly appear in both lists when a claim lands before
  // the to_return query refreshes - the returning entry is fresher, so it
  // wins
  const batches = showParked
    ? parked
    : Array.from(
        new Map([...toReturn, ...returning].map((batch) => [batch.id, batch])).values()
      );
  const isLoading = showParked ? parkedLoading : toReturnLoading || returningLoading;
  const listQueryError = showParked ? parkedError : toReturnError;
  const isListQueryError = showParked ? isParkedError : isToReturnError;

  const refetchAll = useCallback(() => {
    if (showParked) {
      refetchParked();
    } else {
      refetchToReturn();
      refetchReturning();
    }
  }, [showParked, refetchParked, refetchToReturn, refetchReturning]);

  // Web routes remount when navigated to - refresh on mount/toggle
  useEffect(() => {
    refetchAll();
  }, [refetchAll]);

  // Batch pending tote-placement confirmation before returning starts
  const [confirmBatch, setConfirmBatch] = useState(null);
  // Parked batch pending resume confirmation
  const [resumeBatch, setResumeBatch] = useState(null);
  const [listError, setListError] = useState('');

  // The logged-in user's email, for spotting our own in-progress batches
  const [myEmail, setMyEmail] = useState('');
  useEffect(() => {
    getSessionEmail().then(email => setMyEmail(email || ''));
  }, []);

  const returnItemCount = (batch) =>
    (batch.returnItems ?? []).reduce((sum, entry) => sum + (entry.items?.length ?? 0), 0);

  const handleBatchPress = (batch) => {
    if (batch.status === 'returning_parked') {
      // Any user can claim a parked batch and pick the work back up
      setResumeBatch(batch);
      return;
    }
    if (batch.status === 'returning') {
      // Already claimed - only the returner who claimed it can re-enter
      if (batch.returner !== myEmail || !myEmail) return;
      navigate(`/warehouse/app/returns/${batch.id}`);
      return;
    }
    setConfirmBatch(batch);
  };

  // Ask the backend to check the batch out to this returner. It only
  // succeeds while the batch is still 'to_return' and unclaimed.
  const handleConfirmReturnSetup = async () => {
    if (!confirmBatch || isClaiming) return;

    const result = await startReturning(confirmBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setConfirmBatch(null);
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${confirmBatch.name} is already being returned by someone else.`
            : `Could not start returning ${confirmBatch.name}. Please try again.`
      );
      refetchAll();
      return;
    }

    setConfirmBatch(null);
    setListError('');
    navigate(`/warehouse/app/returns/${confirmBatch.id}`);
  };

  // Claiming a parked batch checks it out to this user and flips it back
  // to 'returning'; the saved returning_state is restored on the screen
  const handleConfirmResume = async () => {
    if (!resumeBatch || isResuming) return;

    const result = await resumeReturning(resumeBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setResumeBatch(null);
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${resumeBatch.name} is no longer parked - it may have been claimed or completed already.`
            : `Could not resume returning ${resumeBatch.name}. Please try again.`
      );
      refetchAll();
      return;
    }

    setResumeBatch(null);
    setListError('');
    navigate(`/warehouse/app/returns/${resumeBatch.id}`);
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  const renderBatchCard = (batch) => {
    const isReturning = batch.status === 'returning';
    const isParked = batch.status === 'returning_parked';
    const isMine = isReturning && myEmail !== '' && batch.returner === myEmail;
    const toteCount = (batch.returnItems ?? []).length;
    const itemCount = returnItemCount(batch);
    const disabled = isReturning && !isMine;

    return (
      <div
        key={batch.id}
        className={`warehouse-batch-card${disabled ? ' warehouse-batch-card-disabled' : ''}`}
        {...buttonProps(() => handleBatchPress(batch), { disabled })}
      >
        <div className="warehouse-batch-header">
          <Text as="p" fontWeight="bold">{batch.name}</Text>
        </div>

        <div className="warehouse-batch-statuses">
          {isParked ? (
            <span className="warehouse-badge warehouse-badge-warning">⏸ PARKED</span>
          ) : isReturning ? (
            <span className="warehouse-badge warehouse-badge-primary">
              {isMine ? '▶ RETURNING - RESUME' : 'RETURNING'}
            </span>
          ) : (
            <span className="warehouse-badge warehouse-badge-info">↩ TO RETURN</span>
          )}
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Batch ID:</span>
          <span>{batch.id}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Cart:</span>
          <span>{batch.cartId || 'Unassigned'}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Items to return:</span>
          <span>{itemCount}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Return totes:</span>
          <span>{toteCount}</span>
        </div>

        {isReturning && (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Returner:</span>
            <span>{batch.returner}</span>
          </div>
        )}

        {isParked && batch.returningState?.returner_email ? (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Parked by:</span>
            <span>{batch.returningState.returner_email}</span>
          </div>
        ) : null}

        <div className="warehouse-batch-preview">
          <span className="warehouse-info-label">Return totes</span>
          <BatchGrid
            totalRows={batch.totalRows}
            totalColumns={batch.totalColumns}
            toteSize={24}
            gap={4}
            highlightedTotes={getToteHighlights(batch)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header">
        <div className="warehouse-header-row">
          <div>
            <h2>Returns</h2>
            <div className="warehouse-header-subtitle">
              {showParked
                ? `${batches.length} parked returning batches`
                : `${batches.length} batches in the returns queue`}
            </div>
          </div>
          <div className="warehouse-header-actions">
            <div className="warehouse-switch-label">
              Parked
              <Checkbox checked={showParked} onChange={setShowParked} label="Show parked batches" labelHidden />
            </div>
            <Button onClick={refetchAll} disabled={isLoading} loading={isLoading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {listError ? (
        <Banner tone="critical"><p>{listError}</p></Banner>
      ) : null}

      {isListQueryError ? (
        <Banner tone="critical">
          <p>
            {isAuthError(listQueryError)
              ? SESSION_EXPIRED_MESSAGE
              : showParked
                ? 'Could not load parked batches. Press refresh to try again.'
                : 'Could not load batches. Press refresh to try again.'}
          </p>
        </Banner>
      ) : null}

      <div className="warehouse-list">
        {isLoading && batches.length === 0 ? (
          <div className="warehouse-loading"><LoadingSpinner statusText="Loading batches..." /></div>
        ) : batches.length === 0 ? (
          <div className="warehouse-loading">
            <Text as="p">{showParked ? 'No parked returning batches.' : 'No batches waiting to be returned.'}</Text>
          </div>
        ) : (
          batches.map(renderBatchCard)
        )}
      </div>

      <Modal
        open={confirmBatch !== null}
        onClose={() => setConfirmBatch(null)}
        title="Confirm Totes"
      >
        {confirmBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{confirmBatch.name} ({confirmBatch.id})</Text>
            <Text as="p">
              Confirm that cart <strong>{confirmBatch.cartId || 'unknown'}</strong> is
              in front of you and the highlighted totes are in place, holding {returnItemCount(confirmBatch)} item{returnItemCount(confirmBatch) === 1 ? '' : 's'} to return.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={confirmBatch.totalRows}
                totalColumns={confirmBatch.totalColumns}
                toteCount={confirmBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getToteHighlights(confirmBatch)}
              />
            </div>
            {isClaiming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setConfirmBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmReturnSetup}>Start Returning</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={resumeBatch !== null}
        onClose={() => setResumeBatch(null)}
        title="Resume Returning?"
      >
        {resumeBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{resumeBatch.name} ({resumeBatch.id})</Text>
            <Text as="p">
              Check out this parked batch and continue returning cart{' '}
              <strong>{resumeBatch.cartId || 'unknown'}</strong> from where it was left off.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={resumeBatch.totalRows}
                totalColumns={resumeBatch.totalColumns}
                toteCount={resumeBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getToteHighlights(resumeBatch)}
              />
            </div>
            {isResuming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setResumeBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmResume}>Resume Returning</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>
    </div>
  );
};

export default ReturnBatches;
